import { useEffect, useRef, useState } from 'react';
import { searchAddresses, type AddressSuggestion } from '@bala/core';
import { Input } from './primitives';

/**
 * Champ d'adresse avec recherche live sur le registre belge des adresses
 * (BeSt Address). La valeur reste une simple chaîne — sélectionner une
 * suggestion la remplace par l'adresse formatée, mais on peut aussi taper
 * librement et ignorer les suggestions : aucune adresse saisie à la main
 * n'est jamais rejetée.
 */
export function AddressInput({
  value,
  onChange,
  disabled,
  autoFocus,
  placeholder,
  required,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  required?: boolean;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    const safeQuery = (value ?? '').trim();
    if (safeQuery.length < 3) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      searchAddresses(safeQuery, controller.signal)
        .then((items) => {
          setSuggestions(items);
          setHighlighted(0);
        })
        // Service public sans garantie de disponibilité : une panne ne doit
        // pas empêcher de continuer à taper l'adresse à la main.
        .catch(() => setSuggestions([]));
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const select = (suggestion: AddressSuggestion) => {
    onChange(suggestion.label);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Input
        type="text"
        value={value ?? ''}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (!open || suggestions.length === 0) return;
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((current) => (current + 1) % suggestions.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((current) => (current - 1 + suggestions.length) % suggestions.length);
          } else if (event.key === 'Enter' && suggestions[highlighted]) {
            event.preventDefault();
            select(suggestions[highlighted]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 'var(--z-modal)' as unknown as number,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: 4,
          }}
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.id} role="option" aria-selected={index === highlighted}>
              <button
                type="button"
                onClick={() => select(suggestion)}
                onMouseEnter={() => setHighlighted(index)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: 'var(--r-sm)',
                  border: 'none',
                  background: index === highlighted ? 'var(--surface-hover)' : 'transparent',
                  color: 'var(--text)',
                  font: 'var(--t-subhead)',
                  cursor: 'pointer',
                }}
              >
                {suggestion.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
